/**
 * Add / Edit Admin User Component
 * ------------------------------------
 * Handles:
 * - Creating new admin users
 * - Editing existing admin users
 * - Updating verification status
 * - Managing order types (multi-select)
 * - Resetting form and clearing edit mode
 */

import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    MenuItem,
    Chip,
    Select,
    InputLabel,
    FormControl,
    OutlinedInput,
    Box,
    Grid,
    FormControlLabel,
    Switch,
    SelectChangeEvent,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Autocomplete,
    InputAdornment,
    IconButton,
} from "@mui/material";

import AdminApi from "Service/Admin.api";
import CaseApi from "Service/Case.api";
import AuthApi from "Service/Auth.api";
import { IndianStates } from "Constants/Arrays";
import { StyledTableCell, StyledTableRow, HeaderCard, SearchCard, StyledContainer } from "./ProfessionalUserList";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Typography, Fade, Slide, CardContent, Card } from "@mui/material";

/* ------------------------------
   INTERFACES
------------------------------ */
interface OrderType {
    id: number;
    name: string;
}

interface AdminFormType {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    dateOfBirth: string;
    password?: string;
    confirmPassword?: string;
    highestQualification: string;
    email: string;
    contactNumber: string;
    orderType: number[];
    roleType: string;
    // orderTypeIds: number[];
    verified: boolean;
}

interface OrderTypeItem {
    id: number;
    name: string;
}

interface AdminType {
    id: number;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    dateOfBirth: string;
    email: string;
    contactNumber: string;
    orderTypes?: OrderTypeItem[];
    highestQualification?: string;
    verified?: boolean;
    userType?: string;
}

/* ------------------------------
   MAIN COMPONENT
------------------------------ */

const AddAdminUser: React.FC = () => {
    const navigate = useNavigate();

    /* ----------------------------------
       LOCAL STATE
    ---------------------------------- */
    const [formData, setFormData] = useState<AdminFormType>({
        firstName: "",
        lastName: "",
        address: "",
        city: "",
        state: "",
        postalCode: "",
        dateOfBirth: "",
        password: "",
        confirmPassword: "",
        email: "",
        contactNumber: "",
        highestQualification: "",
        orderType: [],
        roleType: "Admin", // default
        // orderTypeIds: [],
        verified: true, // Default verified ON for new admins
    });

    const [adminList, setAdminList] = useState<AdminType[]>([]);
    const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
    const [editId, setEditId] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    /* ----------------------------------
       EFFECTS - Load Data Once
    ---------------------------------- */
    useEffect(() => {
        loadAdmins();
        loadOrderTypes();
    }, []);

    /* ----------------------------------
       Fetch Admin List
    ---------------------------------- */
    const loadAdmins = async () => {
        try {
            const res = await AdminApi.getAllAdminUsers();
            setAdminList(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error("Error fetching admins:", error);
        }
    };

    /* ----------------------------------
       Fetch Order Type List
    ---------------------------------- */
    const loadOrderTypes = async () => {
        try {
            const res = await CaseApi.fetchOrderTypes();
            setOrderTypes(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error("Error fetching order types:", error);
        }
    };

    /* ----------------------------------
       Handle Basic Form Input Change
    ---------------------------------- */
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    /* ----------------------------------
       Multi-Select (Order Types)
    ---------------------------------- */
    const handleOrderTypeChange = (
        event: SelectChangeEvent<typeof formData.orderType>
    ) => {
        const selectedIds = event.target.value as number[];
        setFormData({ ...formData, orderType: selectedIds });
    };

    /* ----------------------------------
       Verified Toggle
    ---------------------------------- */
    const handleVerifiedToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({
            ...prev,
            verified: event.target.checked,
        }));
    };

    /* ----------------------------------
       Reset Form (Reusable)
    ---------------------------------- */
    const handleReset = () => {
        setFormData({
            firstName: "",
            lastName: "",
            address: "",
            city: "",
            state: "",
            postalCode: "",
            dateOfBirth: "",
            password: "",
            confirmPassword: "",
            email: "",
            contactNumber: "",
            highestQualification: "",
            orderType: [],
            roleType: "Admin",
            // orderTypeIds: [],
            verified: true,
        });
        setEditId(null);
    };

    /* ----------------------------------
       Submit Form (Create / Update)
    ---------------------------------- */
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const payload = {
            ...formData,
            confirmPassword: formData.password,
            orderTypeIds: formData.orderType,
        };

        try {
            if (editId) {
                // If password is not updated, remove it
                if (!payload.password) delete payload.password;

                await AdminApi.UpdateAdminUser(editId, {
                    ...payload,
                    id: editId,
                });
            } else {
                if (payload.roleType === "E_MITRA_USER") {
                    const emitraPayload = {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        password: formData.password || "",
                        email: formData.email,
                        contactNumber: formData.contactNumber,
                        // Default values for fields removed from UI but likely required by backend
                        address: formData.address || "Not Provided",
                        city: formData.city || "Not Provided",
                        state: formData.state || "Not Provided",
                        postalCode: formData.postalCode || "000000",
                        dateOfBirth: formData.dateOfBirth || new Date().toISOString().split("T")[0],
                        description: "New Professional",
                        highestQualification: formData.highestQualification || "Not Provided",
                        profilePicturePath: "",
                        roles: [],
                        skills: [],
                    };
                    await AuthApi.registerEmitraUser(emitraPayload);
                } else {
                    await AdminApi.CreateAdminUser(payload);
                }
            }

            await loadAdmins();
            handleReset();
        } catch (error) {
            console.error("Error saving admin:", error);
        }
    };

    /* ----------------------------------
       Handle Edit (Prefill Form)
    ---------------------------------- */
    const handleEdit = (item: AdminType) => {
        setEditId(item.id);

        setFormData({
            firstName: item.firstName,
            lastName: item.lastName,
            address: item.address,
            city: item.city,
            state: item.state,
            postalCode: item.postalCode,

            // Convert API date -> YYYY-MM-DD
            dateOfBirth: item.dateOfBirth ? item.dateOfBirth.split("T")[0] : "",

            password: "",
            confirmPassword: "",
            email: item.email,
            contactNumber: item.contactNumber,
            highestQualification: item.highestQualification || "",

            // Extract IDs from orderTypes
            orderType: item.orderTypes?.map((o) => o.id) || [],
            roleType: item.userType || "Admin",
            // orderTypeIds: item.orderTypes?.map((o) => o.id) || [],
            verified: item.verified ?? true,
        });
    };

    const filteredAdmins = Array.isArray(adminList) ? adminList.filter(admin =>
        `${admin.firstName} ${admin.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        admin.email.toLowerCase().includes(searchQuery.toLowerCase())
    ) : [];

    /* ----------------------------------
       RENDER
    ---------------------------------- */
    return (
        <StyledContainer maxWidth="xl">
            <Fade in timeout={800}>
                <Box sx={{ width: '100%', pb: 10 }}>
                    {/* Back Button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                            sx={{ fontWeight: 'bold' }}
                        >
                            Back
                        </Button>
                    </Box>

                    {/* Header Section */}
                    <HeaderCard elevation={8} sx={{ mb: 4 }}>
                        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                flexDirection={{ xs: 'column', md: 'row' }}
                                gap={2}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <AdminPanelSettingsIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56 }, color: 'white' }} />
                                    <Box>
                                        <Typography
                                            variant="h4"
                                            component="h1"
                                            fontWeight="bold"
                                            color="white"
                                            gutterBottom
                                        >
                                            Admin Management
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ opacity: 0.9 }}
                                            color="white"
                                        >
                                            Create and manage administrative users and their permissions
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </HeaderCard>

                    <Grid container spacing={4}>
                        {/* Form Section */}
                        <Grid size={{ xs: 12, lg: 12 }}>
                            <Slide direction="right" in timeout={600}>
                                <Paper elevation={4} sx={{ p: 3, borderRadius: 3, borderTop: '4px solid #283165' }}>
                                    <Box display="flex" alignItems="center" gap={1} mb={3}>
                                        <PersonAddIcon color="primary" />
                                        <Typography variant="h6" fontWeight="bold" color="primary">
                                            {editId ? "Edit Admin User" : "Add New Admin"}
                                        </Typography>
                                    </Box>

                                    <form onSubmit={handleSubmit}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} required fullWidth size="small" />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} required fullWidth size="small" />
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <TextField label="Email" name="email" type="email" value={formData.email} onChange={handleChange} required fullWidth size="small" />
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <TextField label="Contact Number" name="contactNumber" value={formData.contactNumber} onChange={handleChange} required fullWidth size="small" />
                                            </Grid>

                                            <Grid size={{ xs: 12 }}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>User Role</InputLabel>
                                                    <Select
                                                        name="roleType"
                                                        value={formData.roleType}
                                                        label="User Role"
                                                        onChange={(e) => setFormData({ ...formData, roleType: e.target.value as string })}
                                                    >
                                                        <MenuItem value="Admin">Admin</MenuItem>
                                                        <MenuItem value="E_MITRA_USER">E-Mitra User</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            {formData.roleType === "Admin" && (
                                                <Grid size={{ xs: 12 }}>
                                                    <Autocomplete
                                                        multiple
                                                        options={orderTypes}
                                                        getOptionLabel={(option) => option.name}
                                                        value={Array.isArray(orderTypes) ? orderTypes.filter((ot) => formData.orderType.includes(ot.id)) : []}
                                                        onChange={(e, newValue) => {
                                                            setFormData({ ...formData, orderType: newValue.map((v) => v.id) });
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField {...params} label="Assigned Permissions" placeholder="Select Order Types" size="small" />
                                                        )}
                                                        renderTags={(value, getTagProps) =>
                                                            value.map((option, index) => (
                                                                <Chip label={option.name} size="small" {...getTagProps({ index })} />
                                                            ))
                                                        }
                                                    />
                                                </Grid>
                                            )}

                                            {!editId && (
                                                <>
                                                    <Grid size={{ xs: 12 }}>
                                                        <TextField
                                                            label="Password"
                                                            name="password"
                                                            type={showPassword ? "text" : "password"}
                                                            value={formData.password}
                                                            onChange={handleChange}
                                                            fullWidth
                                                            size="small"
                                                            slotProps={{
                                                                input: {
                                                                    endAdornment: (
                                                                        <InputAdornment position="end">
                                                                            <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    ),
                                                                },
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <TextField
                                                            label="Confirm Password"
                                                            name="confirmPassword"
                                                            type={showPassword ? "text" : "password"}
                                                            value={formData.confirmPassword}
                                                            onChange={handleChange}
                                                            fullWidth
                                                            size="small"
                                                        />
                                                    </Grid>
                                                </>
                                            )}

                                            {editId && (
                                                <Grid size={{ xs: 12 }}>
                                                    <FormControlLabel
                                                        control={<Switch checked={formData.verified} onChange={handleVerifiedToggle} color="success" />}
                                                        label={formData.verified ? "Account Active" : "Account Suspended"}
                                                    />
                                                </Grid>
                                            )}

                                            <Grid size={{ xs: 12 }} sx={{ mt: 2, display: 'flex', gap: 2 }}>
                                                <Button type="submit" variant="contained" color="primary" fullWidth sx={{ borderRadius: 2 }}>
                                                    {editId ? "Update" : "Create"}
                                                </Button>
                                                <Button type="button" variant="outlined" color="inherit" fullWidth onClick={handleReset} sx={{ borderRadius: 2 }}>
                                                    Cancel
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </Paper>
                            </Slide>
                        </Grid>

                        {/* List Section */}
                        <Grid size={{ xs: 12, lg: 12 }}>
                            <Slide direction="left" in timeout={600}>
                                <Box>
                                    <SearchCard elevation={2} sx={{ mb: 3 }}>
                                        <CardContent sx={{ p: '12px !important' }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                placeholder="Search by name or email..."
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon color="primary" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                            />
                                        </CardContent>
                                    </SearchCard>

                                    <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                        <TableContainer sx={{ maxHeight: 600 }}>
                                            <Table stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <StyledTableCell>Admin User</StyledTableCell>
                                                        <StyledTableCell>Contact</StyledTableCell>
                                                        <StyledTableCell>Permissions</StyledTableCell>
                                                        <StyledTableCell>Status</StyledTableCell>
                                                        <StyledTableCell align="center">Action</StyledTableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {filteredAdmins.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                                                <Typography color="textSecondary">No admins found matching your search.</Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        filteredAdmins.map((user: any) => (
                                                            <StyledTableRow key={user.id}>
                                                                <TableCell>
                                                                    <Box>
                                                                        <Typography variant="subtitle2" fontWeight="bold">
                                                                            {user.firstName} {user.lastName}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.primary">
                                                                            {user.email}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>{user.contactNumber}</TableCell>
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {user?.orderTypes?.length > 0 ? (
                                                                            user.orderTypes.map((t: any) => (
                                                                                <Chip key={t.id} label={t.name} size="small" variant="outlined" />
                                                                            ))
                                                                        ) : (
                                                                            <Typography variant="caption" color="text.primary">None</Typography>
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={user.verified ? "Active" : "Inactive"}
                                                                        color={user.verified ? "success" : "error"}
                                                                        size="small"
                                                                        variant="filled"
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        onClick={() => handleEdit(user)}
                                                                        sx={{ borderRadius: 2, textTransform: 'none' }}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                </TableCell>
                                                            </StyledTableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </Box>
                            </Slide>
                        </Grid>
                    </Grid>
                </Box>
            </Fade>
        </StyledContainer>
    );
};

export default AddAdminUser;